import {useEffect, useState} from 'react';

/**
 * Seconds left until an ISO instant, re-rendering once a second until it reaches zero.
 *
 * Used for the transcription session's reset. Returns `null` when there is nothing to count down
 * to, which is the case that matters: an **inactive** session has a null `expiresAt`, and the
 * caller must render no timer at all rather than a zero — the user's clock has not started and
 * showing one would invent a deadline they are not under.
 *
 * The interval is dropped once the target passes, so an expired countdown costs nothing while it
 * sits on screen waiting for the next usage read to replace it.
 */
export function useCountdown(targetIso: string | null | undefined): number | null {
  const targetMs = targetIso ? new Date(targetIso).getTime() : NaN;
  const isValid = Number.isFinite(targetMs);

  const [secondsLeft, setSecondsLeft] = useState<number | null>(() =>
    (isValid ? Math.max(0, Math.round((targetMs - Date.now()) / 1000)) : null)
  );

  useEffect(() => {
    if (!isValid) {
      setSecondsLeft(null);
      return;
    }

    const read = () => Math.max(0, Math.round((targetMs - Date.now()) / 1000));

    setSecondsLeft(read());

    // Recomputed from the wall clock every tick rather than decremented, so a backgrounded tab —
    // where timers are throttled to once a minute — comes back with the right number instead of one
    // that has fallen behind by however long the user was away.
    const intervalId = setInterval(() => {
      const next = read();
      setSecondsLeft(next);

      if (next <= 0) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [targetMs, isValid]);

  return secondsLeft;
}

export default useCountdown;
